import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { NavigationMenu } from '@modules/shared/schemas/navigation-menu.schema';
import { Breadcrumb } from '@modules/shared/services/breadcrumb.service';
import { HeaderService } from '@modules/shared/services/header.service';
import { MagazinePageService } from '@pages/magazine-page/magazine-page.service';
import { catchError, EMPTY, map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TopicResolver implements Resolve<Observable<NavigationMenu | undefined>> {
  constructor(private headerService: HeaderService) {}

  public resolve(route: ActivatedRouteSnapshot): Observable<NavigationMenu | undefined> {
    return this.headerService.findNavigationMenuByPath(fullTopicPathFromRoute(route));
  }
}

@Injectable({
  providedIn: 'root',
})
export class TopicBreadcrumbResolver implements Resolve<Observable<Breadcrumb[] | Breadcrumb | undefined>> {
  constructor(private headerService: HeaderService) {}

  public resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[] | Breadcrumb | undefined> {
    // find topic tree item for path
    return this.headerService.findNavigationMenuByPath(fullTopicPathFromRoute(route)).pipe(
      switchMap((menu) => {
        if (menu) {
          // build level tree and map to breadcrumbs
          return this.headerService.navigationMenuPath$(menu).pipe(
            map((topicPath: NavigationMenu[]) => {
              return topicPath.map((item) => ({ label: `MENU.${item.id}.NAME`, link: `MENU.${item.id}.PATH` }));
            })
          );
        } else {
          return of(undefined);
        }
      })
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class TopicAndMagazineBreadcrumbResolver implements Resolve<Observable<Breadcrumb[] | Breadcrumb | undefined>> {
  constructor(
    private headerService: HeaderService,
    private magazinePageService: MagazinePageService,
    private router: Router
  ) {}

  public resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[] | Breadcrumb | undefined> {
    // find topic tree item for path
    return this.headerService.findNavigationMenuByPath(fullTopicPathFromRoute(route)).pipe(
      switchMap((menu) => {
        if (menu) {
          // build level tree and map to breadcrumbs
          return this.headerService.navigationMenuPath$(menu).pipe(
            map((topicPath: NavigationMenu[]) => {
              return topicPath.map((item) => ({ label: `MENU.${item.id}.NAME`, link: `MENU.${item.id}.PATH` }));
            })
          );
        } else {
          // is page or event page
          // TODO: find nicer solution, than requesting the page again
          const slug = route.paramMap.get('level1') || route.paramMap.get('slug');
          if (slug) {
            return this.magazinePageService.getMagazine(slug).pipe(
              map((magazine) => {
                return { label: magazine?.title || 'MEDIA_SPECIAL', link: slug };
              })
            );
          } else {
            // home page has no breadbrumbs
            return of(undefined);
          }
        }
      }),
      catchError((error) => {
        if (error.status === 401) {
          this.router.navigate(['401'], { skipLocationChange: true });
        }
        if (error.status === 403) {
          this.router.navigate(['403'], { skipLocationChange: true });
        }
        if (error.status === 404) {
          this.router.navigate(['404'], { skipLocationChange: true });
        }
        return EMPTY;
      })
    );
  }
}

const fullTopicPathFromRoute = (route: ActivatedRouteSnapshot): string => {
  const level1 = route.paramMap.get('level1') || route.paramMap.get('slug') || 'home';
  const level2 = route.paramMap.get('level2') || undefined;
  const level3 = route.paramMap.get('level3') || undefined;
  const level4 = route.paramMap.get('level4') || undefined;
  return '/' + [level1, level2, level3, level4].filter((l) => l !== undefined).join('/');
};
